import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { useScanners } from "@/hooks/useScanners";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Cloud, 
  Database, 
  Shield, 
  Github, 
  Settings,
  Plus
} from "lucide-react";

interface IntegrationConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface IntegrationFormData {
  name: string;
  type: 'aws' | 'gcp' | 'azure' | 'okta' | 'github' | 'hetzner';
  config: any;
}

export function IntegrationConfigDialog({ open, onOpenChange }: IntegrationConfigDialogProps) {
  const [activeTab, setActiveTab] = useState("aws");
  const [loading, setLoading] = useState(false);
  const { connectIntegration } = useScanners();

  const form = useForm<IntegrationFormData>({
    defaultValues: {
      name: "",
      type: "aws",
      config: {}
    }
  });

  const onSubmit = async (data: IntegrationFormData) => {
    try {
      setLoading(true);
      await connectIntegration(data.type, data.config, data.name);
      toast.success("Integration connected successfully!");
      onOpenChange(false);
      form.reset();
    } catch (error: any) {
      toast.error(error?.message || "Error connecting integration");
    } finally {
      setLoading(false);
    }
  };

  const integrationTypes = [
    {
      id: "aws",
      name: "Amazon Web Services",
      icon: <Cloud className="h-5 w-5" />,
      description: "Scan AWS infrastructure for security issues",
      fields: [
        { name: "access_key_id", label: "Access Key ID", type: "text", required: true },
        { name: "secret_access_key", label: "Secret Access Key", type: "password", required: true },
        { name: "region", label: "Default Region", type: "text", placeholder: "us-east-1", required: true },
        { name: "role_arn", label: "Role ARN (Optional)", type: "text", required: false }
      ]
    },
    {
      id: "gcp",
      name: "Google Cloud Platform",
      icon: <Cloud className="h-5 w-5" />,
      description: "Scan GCP resources for security vulnerabilities",
      fields: [
        { name: "project_id", label: "Project ID", type: "text", required: true },
        { name: "service_account_key", label: "Service Account Key (JSON)", type: "textarea", required: true },
        { name: "region", label: "Default Region", type: "text", placeholder: "us-central1", required: false }
      ]
    },
    {
      id: "azure",
      name: "Microsoft Azure",
      icon: <Cloud className="h-5 w-5" />,
      description: "Scan Azure infrastructure for security issues",
      fields: [
        { name: "client_id", label: "Client ID", type: "text", required: true },
        { name: "client_secret", label: "Client Secret", type: "password", required: true },
        { name: "tenant_id", label: "Tenant ID", type: "text", required: true },
        { name: "subscription_id", label: "Subscription ID", type: "text", required: true }
      ]
    },
    {
      id: "okta",
      name: "Okta",
      icon: <Shield className="h-5 w-5" />,
      description: "Scan Okta identity configuration",
      fields: [
        { name: "domain", label: "Okta Domain", type: "text", placeholder: "yourcompany.okta.com", required: true },
        { name: "api_token", label: "API Token", type: "password", required: true }
      ]
    },
    {
      id: "github",
      name: "GitHub",
      icon: <Github className="h-5 w-5" />,
      description: "Scan GitHub repositories for security vulnerabilities",
      fields: [
        { name: "token", label: "Personal Access Token", type: "password", required: true },
        { name: "organization", label: "Organization (Optional)", type: "text", required: false }
      ]
    },
    {
      id: "hetzner",
      name: "Hetzner Cloud",
      icon: <Cloud className="h-5 w-5" />,
      description: "Scan Hetzner dedicated and cloud servers",
      fields: [
        { name: "api_token", label: "API Token", type: "password", required: true },
        { name: "project_name", label: "Project Name (Optional)", type: "text", required: false }
      ]
    }
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Configure New Integration
          </DialogTitle>
          <DialogDescription>
            Connect a new scanning integration to monitor your infrastructure
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-6">
            {integrationTypes.map((type) => (
              <TabsTrigger key={type.id} value={type.id} className="flex items-center gap-1 text-xs">
                {type.icon}
                <span className="hidden sm:inline">{type.name.split(' ')[0]}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {integrationTypes.map((integrationType) => (
            <TabsContent key={integrationType.id} value={integrationType.id}>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    {integrationType.icon}
                    {integrationType.name}
                  </CardTitle>
                  <CardDescription>{integrationType.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Integration Name</FormLabel>
                            <FormControl>
                              <Input 
                                placeholder={`My ${integrationType.name} Integration`} 
                                {...field} 
                              />
                            </FormControl>
                            <FormDescription>
                              A recognizable name for this integration
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {integrationType.fields.map((fieldConfig) => (
                        <FormField
                          key={fieldConfig.name}
                          control={form.control}
                          name={`config.${fieldConfig.name}` as any}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>
                                {fieldConfig.label}
                                {fieldConfig.required && <span className="text-red-500 ml-1">*</span>}
                              </FormLabel>
                              <FormControl>
                                {fieldConfig.type === "textarea" ? (
                                  <Textarea
                                    placeholder={fieldConfig.placeholder}
                                    rows={4}
                                    {...field}
                                  />
                                ) : (
                                  <Input
                                    type={fieldConfig.type}
                                    placeholder={fieldConfig.placeholder}
                                    {...field}
                                  />
                                )}
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      ))}

                      <div className="flex justify-end gap-2 pt-4">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => onOpenChange(false)}
                        >
                          Annuleren
                        </Button>
                        <Button
                          type="submit"
                          disabled={loading}
                          onClick={() => {
                            form.setValue("type", integrationType.id as any);
                          }}
                        >
                          {loading ? "Connecting..." : "Connect Integration"}
                        </Button>
                      </div>
                    </form>
                  </Form>
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}